import { z } from 'zod';

// --- Participant ----------------------------------------------------

/** Full Peppol participant identifier, e.g. `iso6523-actorid-upis::0151:51824753556`. */
export const PeppolParticipantIdSchema = z
  .string()
  .regex(/^iso6523-actorid-upis::\d{4}:[A-Za-z0-9._-]+$/);
export type PeppolParticipantId = z.infer<typeof PeppolParticipantIdSchema>;

/** Authority scheme (Peppol Codelist): 0151 = AU:ABN, 0088 = GLN, 0195 = SG:UEN, 0230 = MY:EIF. */
export const ParticipantSchemeSchema = z.string().regex(/^\d{4}$/);
export type ParticipantScheme = z.infer<typeof ParticipantSchemeSchema>;

export const ParticipantStatusSchema = z.enum([
  'PENDING',
  'KYC_REQUIRED',
  'REGISTERED',
  'ACTIVATED',
  'PUBLISHED',
  'DEACTIVATED',
  'DELETED',
]);
export type ParticipantStatus = z.infer<typeof ParticipantStatusSchema>;

export const ParticipantSchema = z.object({
  participantId: PeppolParticipantIdSchema,
  scheme: ParticipantSchemeSchema,
  identifier: z.string(),
  legalName: z.string(),
  country: z.string().length(2),
  status: ParticipantStatusSchema,
  smlActive: z.boolean(),
  publishedInDirectory: z.boolean(),
  endpointId: z.string().optional(),
  documentProfiles: z.array(z.string()).default([]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Participant = z.infer<typeof ParticipantSchema>;

// --- Transaction ----------------------------------------------------

export const TransactionDirectionSchema = z.enum(['OUTBOUND', 'INBOUND']);
export type TransactionDirection = z.infer<typeof TransactionDirectionSchema>;

/** Tickstar Transaction API state values. */
export const TransactionStateSchema = z.enum([
  'PROCESSING',
  'QUARANTINED',
  'COMPLETED',
  'ACKNOWLEDGED',
  'PURGED',
  'MANUAL_ERROR',
]);
export type TransactionState = z.infer<typeof TransactionStateSchema>;

export const TransactionStatusSchema = z.enum(['SUCCESSFUL', 'FAILED']);
export type TransactionStatus = z.infer<typeof TransactionStatusSchema>;

export const ArtefactTypeSchema = z.enum([
  'INBOUND_SOURCE',
  'OUTBOUND_SOURCE',
  'VALIDATION_RESULT',
  'C2_C3_AP_RESPONSE',
  'AS4_RECEIPT',
]);
export type ArtefactType = z.infer<typeof ArtefactTypeSchema>;

export const TransactionArtefactSchema = z.object({
  artefactId: z.string(),
  type: ArtefactTypeSchema,
  contentType: z.string(),
  sizeBytes: z.number().nonnegative(),
});
export type TransactionArtefact = z.infer<typeof TransactionArtefactSchema>;

export const TransactionSchema = z.object({
  transactionId: z.string(),
  messageIdentifier: z.string().uuid(),
  direction: TransactionDirectionSchema,
  state: TransactionStateSchema,
  status: TransactionStatusSchema.optional(),
  documentType: z.string(),
  corner1ParticipantId: PeppolParticipantIdSchema.optional(),
  corner4ParticipantId: PeppolParticipantIdSchema.optional(),
  senderParticipantId: PeppolParticipantIdSchema,
  receiverParticipantId: PeppolParticipantIdSchema,
  createdAt: z.string().datetime(),
  completedAt: z.string().datetime().optional(),
  acknowledgedAt: z.string().datetime().optional(),
  artefacts: z.array(TransactionArtefactSchema).default([]),
  errorCode: z.string().optional(),
  errorMessage: z.string().optional(),
});
export type Transaction = z.infer<typeof TransactionSchema>;

// --- Document send/receive payload ---------------------------------

export const SendDocumentInputSchema = z.object({
  /** Full SBDH-wrapped UBL XML, ready to ship over Peppol. */
  payload: z.string(),
  /** Optional client-supplied idempotency key (will dedupe within Tickstar). */
  idempotencyKey: z.string().optional(),
  /** Override the network type. Defaults to `PEPPOL`. */
  networkType: z.enum(['PEPPOL']).default('PEPPOL'),
});
export type SendDocumentInput = z.infer<typeof SendDocumentInputSchema>;

export const SendDocumentResultSchema = z.object({
  transactionId: z.string(),
  messageIdentifier: z.string().uuid(),
  acceptedAt: z.string().datetime(),
});
export type SendDocumentResult = z.infer<typeof SendDocumentResultSchema>;

// --- Webhook event --------------------------------------------------

export const WebhookEventSchema = z.object({
  transactionId: z.string(),
  organisationId: z.string(),
  direction: TransactionDirectionSchema,
  networkType: z.string(),
  metadata: z.object({
    messageIdentifier: z.string(),
    networkProfile: z.string().optional(),
    corner1Identifier: PeppolParticipantIdSchema.optional(),
    corner4Identifier: PeppolParticipantIdSchema.optional(),
    documentIdentifier: z.string().optional(),
  }),
  state: TransactionStateSchema,
  status: TransactionStatusSchema.optional(),
  /** Optional inline artefact when webhook is configured with multipart delivery. */
  inboundSource: z.string().optional(),
  receivedAt: z.string().datetime(),
});
export type WebhookEvent = z.infer<typeof WebhookEventSchema>;

// --- Region & connectivity -----------------------------------------

export const ProviderRegionSchema = z.enum(['TEST', 'EU', 'ANZ', 'SEA']);
export type ProviderRegion = z.infer<typeof ProviderRegionSchema>;

export interface ProviderConnectivity {
  region: ProviderRegion;
  /** Whether the provider is hitting live infrastructure or returning mock data. */
  mode: 'live' | 'mock';
  /** Human-readable label for UI badges, e.g. "TickStar EU (live)" or "MOCK · sample data". */
  label: string;
  /** When `true`, the UI should render a "DEMO / MOCK" pill next to provider data. */
  showDemoBadge: boolean;
}
